import { createFileRoute } from '@tanstack/react-router';

// Public privacy-policy page (no auth gate) — matches docs/Privacy-Policy.md
// word for word. Keep both in sync when you update the policy: the .md is the
// auditor / Plaid-attachment artifact; this file is what end users actually see
// at https://command.batemanholdingsllc.com/privacy.

export const Route = createFileRoute('/privacy')({
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', padding: '32px 16px 64px' }}>
      <article
        style={{
          maxWidth: 760,
          margin: '0 auto',
          background: 'var(--bg-surface)',
          color: 'var(--text-default)',
          padding: '40px 48px',
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          lineHeight: 1.55,
          fontSize: 14,
        }}
      >
        <header style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 28, marginTop: 0, marginBottom: 16, color: 'var(--text-strong)' }}>
            Privacy Policy
          </h1>
          <Meta label="Organization" value="Bateman Holdings LLC" />
          <Meta label="Service" value="Multi-Company Accounting System (the “Application”)" />
          <Meta label="Web address" value="https://command.batemanholdingsllc.com" />
          <Meta label="Contact" value="ken@batemanholdingsllc.com" />
          <Meta label="Effective date" value="2026-05-26" />
          <Meta label="Last updated" value="2026-05-26" />
        </header>
        <Hr />

        <H2>1. Who we are</H2>
        <P>
          Bateman Holdings LLC (“we”, “us”, “our”) operates the Multi-Company Accounting System
          (the “Application”). The Application is an internal accounting and bookkeeping tool used
          by the Owner of Bateman Holdings LLC to maintain the books for the companies the Owner
          controls. This privacy policy explains what information the Application processes, how it
          is used, who has access to it, and how it is protected.
        </P>

        <H2>2. Who uses the Application</H2>
        <P>
          The Application is a single-user system. The only authorized user is the Owner of Bateman
          Holdings LLC. There are no third-party customers, end-users, or guest accounts.
          References below to “you” mean the Owner in the Owner’s capacity as the user of the
          Application.
        </P>

        <H2>3. Information we collect</H2>
        <P>
          We collect only the information necessary to maintain the books of the companies the
          Owner controls.
        </P>
        <Table
          headers={['Category', 'Examples', 'Source']}
          rows={[
            ['Account identity', 'Your Microsoft Entra ID Object ID, name, email', 'Microsoft Entra ID at sign-in'],
            [
              'Bank-account credentials',
              'Plaid access_token (an opaque token that authorizes our Application to retrieve transactions from your bank via Plaid — not your bank username or password)',
              'Plaid Link, when you connect a bank account',
            ],
            ['Bank-account metadata', 'Account name, account-number mask, account type, institution name', 'Plaid'],
            ['Bank transaction data', 'Transaction date, amount, description, merchant name, pending status, Plaid category', 'Plaid /transactions/sync'],
            ['Bookkeeping data', 'Journal entries, reconciliations, invoices, bills, checks, deposits, customer/vendor records, payroll entries you create', 'You, when you use the Application'],
            ['Bank statement documents', 'PDF or CSV statements you upload as records of account', 'You'],
            ['AI-assist prompts and responses', 'The text of conversational queries you send to the AI assistant and the AI’s responses', 'You + Azure OpenAI'],
            ['Operational logs', 'Authenticated request metadata (timestamp, route, status code, your Entra Object ID)', 'Generated automatically'],
            ['Audit log', 'Every create, update, or delete on a financial entity, with before/after snapshots and the actor’s Entra Object ID', 'Generated automatically'],
          ]}
        />
        <P>
          We do not collect: your bank’s login password (Plaid never shares it with us), your
          Social Security number, payment-card numbers, biometric data, location data, advertising
          identifiers, or any data about anyone other than you and the entities you transact with
          through your business.
        </P>

        <H2>4. How we use the information</H2>
        <P>We use the information solely to:</P>
        <Ol items={[
          'Authenticate you and authorize your access to the Application.',
          'Ingest and categorize your bank transactions.',
          'Maintain the double-entry general ledger and produce the financial reports you request (balance sheet, income statement, cash flow, reconciliations, etc.).',
          'Reconcile bank statements against the ledger and store the statement documents.',
          'Generate AI-assisted suggestions (transaction categorization, reconciliation matching, conversational Q&A) — only when you initiate the request.',
          'Maintain an audit trail and a tamper-evident hash chain over completed reconciliations for accounting integrity.',
        ]} />
        <P>
          We do not use your information to serve advertisements, train AI models that we sell, or
          build user profiles for any purpose outside the operation of the Application.
        </P>

        <H2>5. Who we share information with</H2>
        <P>
          We share information only with the providers (“sub-processors”) strictly required to
          operate the Application. We do not sell, rent, or trade your information to anyone, and
          we do not share it with marketers, data brokers, or analytics vendors.
        </P>
        <Table
          headers={['Sub-processor', 'Service provided', 'What is shared', 'Provider’s privacy notice']}
          rows={[
            [
              'Microsoft Corporation (Azure)',
              'Hosting, identity (Entra ID), database, object storage, secrets management, AI (Azure OpenAI)',
              'All Application data; Azure stores and processes it under our control',
              <A href="https://privacy.microsoft.com/privacystatement">privacy.microsoft.com</A>,
            ],
            [
              'Plaid Inc.',
              'Bank-account linking and transaction sync',
              'The Plaid access_token for accounts you connect; no end-user data is sent to Plaid beyond what Plaid needs to identify the linked account',
              <A href="https://plaid.com/legal/#end-user-privacy-policy">plaid.com/legal</A>,
            ],
          ]}
        />
        <P>
          Microsoft acts as our data processor for the Azure-hosted data (i.e., it stores and
          serves data on our behalf, not for its own purposes). Plaid acts as a separate controller
          for the data it independently collects from your bank.
        </P>
        <P>
          We also rely on Microsoft (Azure DevOps) for source-code hosting and continuous
          integration; no Application data is shared with that service.
        </P>

        <H2>6. Plaid disclosure</H2>
        <P>
          The Application uses Plaid Inc. (“Plaid”) to connect to your financial accounts at your
          bank and to retrieve transaction information on an ongoing basis. By using the
          Application and linking a bank account, you grant the Application and Plaid the right,
          power, and authority to act on your behalf to access and transmit your personal and
          financial information from your bank, as described in{' '}
          <A href="https://plaid.com/legal/#end-user-privacy-policy">Plaid’s End User Privacy Policy</A>.
        </P>
        <P>
          You may revoke Plaid’s access to your bank at any time through Plaid’s portal at{' '}
          <A href="https://my.plaid.com/">my.plaid.com</A>, or by disconnecting the account inside
          the Application — both actions remove our ability to retrieve further transactions from
          that account.
        </P>

        <H2>7. How we protect the information</H2>
        <P>The Application implements multiple layers of security controls:</P>
        <Ul items={[
          <><B>Identity and access.</B> All sign-ins use Microsoft Entra ID with multi-factor authentication enforced. The backend enforces a server-side allowlist of authorized Entra Object IDs on every request.</>,
          <><B>Encryption in transit.</B> All HTTP is HTTPS with TLS 1.2 or higher. HSTS is enabled in the browser. Database connections require TLS at the server level.</>,
          <><B>Encryption at rest.</B> All Azure storage layers (Postgres, Blob Storage, Key Vault) use AES-256 encryption with Microsoft-managed keys. The Plaid access_token receives an additional layer of AES-256-GCM application-level encryption using a key kept in Azure Key Vault.</>,
          <><B>Network isolation.</B> The database and Key Vault have public network access disabled and are reachable only through private VNet endpoints. The bank-statement storage account has account-key access disabled and a default-Deny network rule.</>,
          <><B>Service-to-service authentication.</B> All Azure-to-Azure calls use the Application’s Managed Identity (short-lived OAuth tokens). There are no static service-account credentials.</>,
          <><B>Immutable records.</B> Bank statement documents are stored under a Locked time-based immutability policy plus legal hold. The reconciliation hash chain is append-only, protected by a database trigger.</>,
          <><B>Audit logging.</B> Every create, update, and delete on a financial entity writes an immutable audit row with the actor and a before/after snapshot.</>,
          <><B>Vulnerability management.</B> Dependency advisories at HIGH or CRITICAL severity block CI builds. Container images are scanned with Trivy before each deploy.</>,
        ]} />

        <H2>8. Data retention</H2>
        <Table
          headers={['Category', 'Retention']}
          rows={[
            ['Plaid access_token', 'Retained while the account is connected. When you disconnect, the token is removed from the database and Plaid’s /item/remove is invoked.'],
            ['Bank transaction data', 'Retained for the lifetime of the books — typically a minimum of seven (7) years to satisfy tax-record obligations.'],
            ['Bank statement documents', 'Permanent retention with legal hold (Owner’s default). Statements cannot be deleted by anyone until the legal hold and immutability policy are explicitly released.'],
            ['Audit log', 'Retained for the lifetime of the books. Append-only by design.'],
            ['Reconciliation hash chain', 'Permanent. Database-level trigger rejects modifications or deletions.'],
            ['Database backups', '30 days (Postgres point-in-time-restore window).'],
            ['Operational logs', 'Retained per the Azure platform default (typically 30–90 days) and longer in Log Analytics where applicable.'],
          ]}
        />

        <H2>9. Your rights</H2>
        <P>
          Because you are the Owner of Bateman Holdings LLC and the sole user of the Application,
          you already have direct, unrestricted access to all data the Application processes about
          you. You may:
        </P>
        <Ul items={[
          <><B>Access</B> any data the Application holds about you at any time through the Application’s interface.</>,
          <><B>Correct</B> data through the Application’s edit and journal-entry workflows (with the standard accounting guardrails: completed reconciliations are append-only; voiding creates a new reversing record rather than altering the original).</>,
          <><B>Delete</B> specific records through the Application’s delete paths, subject to accounting integrity controls.</>,
          <><B>Export</B> your data through the Application’s report-export features (XLSX, PDF, CSV).</>,
          <><B>Disconnect a bank</B> at any time, which revokes Plaid’s access to that bank and stops further transaction ingestion for that account.</>,
        ]} />
        <P>
          To make a request that the Application’s interface does not support, contact{' '}
          <A href="mailto:ken@batemanholdingsllc.com">ken@batemanholdingsllc.com</A>.
        </P>

        <H2>10. Children</H2>
        <P>
          The Application is not directed to or intended for use by children under the age of 13,
          and we do not knowingly collect information from anyone under 13.
        </P>

        <H2>11. International transfers</H2>
        <P>
          The Application is hosted in Microsoft Azure’s <B>East US 2</B> region (United States).
          Data is stored and processed in the United States. The sub-processors listed in §5 may
          transfer data within their own global networks subject to their respective privacy
          notices.
        </P>

        <H2>12. Changes to this policy</H2>
        <P>
          We may update this policy from time to time. Material changes will be noted in the “Last
          updated” date at the top of this document and will be made available through the
          Application before they take effect.
        </P>

        <H2>13. Contact</H2>
        <P>
          Questions, concerns, or requests under this policy:{' '}
          <B>Bateman Holdings LLC</B> —{' '}
          <A href="mailto:ken@batemanholdingsllc.com">ken@batemanholdingsllc.com</A>.
        </P>
      </article>
    </div>
  );
}

// ── Small, page-local primitives ─────────────────────────────────────────────

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
      <span style={{ fontWeight: 600, color: 'var(--text-default)' }}>{label}:</span> {value}
    </div>
  );
}

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2 style={{ fontSize: 18, marginTop: 28, marginBottom: 8, color: 'var(--text-strong)' }}>
      {children}
    </h2>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p style={{ margin: '0 0 12px' }}>{children}</p>;
}

function B({ children }: { children: React.ReactNode }) {
  return <strong style={{ color: 'var(--text-strong)' }}>{children}</strong>;
}

function A({ href, children }: { href: string; children: React.ReactNode }) {
  const external = !href.startsWith('mailto:');
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer noopener' : undefined}
      style={{ color: 'var(--accent, #2563eb)' }}
    >
      {children}
    </a>
  );
}

function Hr() {
  return <hr style={{ border: 0, borderTop: '1px solid var(--border, #e5e7eb)', margin: '20px 0' }} />;
}

function Ul({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ margin: '0 0 12px', paddingLeft: 20 }}>
      {items.map((it, i) => (
        <li key={i} style={{ marginBottom: 6 }}>{it}</li>
      ))}
    </ul>
  );
}

function Ol({ items }: { items: React.ReactNode[] }) {
  return (
    <ol style={{ margin: '0 0 12px', paddingLeft: 22 }}>
      {items.map((it, i) => (
        <li key={i} style={{ marginBottom: 6 }}>{it}</li>
      ))}
    </ol>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: React.ReactNode[][] }) {
  return (
    <div style={{ overflowX: 'auto', margin: '0 0 12px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                style={{
                  textAlign: 'left',
                  fontWeight: 600,
                  background: 'var(--bg-surface-2, #f3f6fb)',
                  color: 'var(--text-strong)',
                  padding: '8px 10px',
                  borderBottom: '1px solid var(--border, #e5e7eb)',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  style={{
                    padding: '8px 10px',
                    borderBottom: '1px solid var(--border, #e5e7eb)',
                    verticalAlign: 'top',
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
